import { decodeToken } from '../user/token';

import { getGDPRConsent } from './getGdprConsent';
import { trackLiveRampAction } from './liverampNewRelicTracking';

const SOURCE_ENVELOPE_LIVE_RAMP = 19;
const SOURCE_PAIRID = 25;
const SOURCE_ATSD = 26;
const ENVELOPE_EXPIRY_IN_MILLISECONDS = 1296000000; // fifteen days
const ENVELOP_NO_CONTENT_HTTP_STATUS = 204;
const ENVELOP_SOURCE = 'envelopeLiveramp';
const PAIRID = 'pairIds';
const ATSD = 'atsDirect';

function getLiveRampEnvelops() {
    // eslint-disable-next-line @applitools/compat/no-async-await
    async function _invokeLiveRampAPI(
        rampIdExpiryInMillis: number | null,
        isPairIdEnabled: boolean | null,
        isATSDEnabled: boolean | null,
    ) {
        const apiStartTime = Date.now();

        try {
            const gdprConsent = getGDPRConsent(window);
            const liveRampUrl =
                window.dazn.startupData.ServiceDictionary.LiveRamp.Versions.v1
                    .ServicePath;
            const partnerToken = (global as any).PARTNER_TOKEN;
            if (!partnerToken) {
                trackLiveRampAction('LiveRamp_API_Failed', {
                    reason: 'no_partner_token',
                    duration: Date.now() - apiStartTime,
                });
                return;
            }
            const { hashEmail } = decodeToken(partnerToken);

            if (!liveRampUrl || !hashEmail) {
                trackLiveRampAction('LiveRamp_API_Failed', {
                    reason: 'invalid_inputs',
                    hasUrl: !!liveRampUrl,
                    hasEmail: !!hashEmail,
                    duration: Date.now() - apiStartTime,
                });
                return;
            }
            const urlWithParams = buildQueryParam(
                liveRampUrl,
                gdprConsent,
                hashEmail,
            );
            const response = await doLiveRampApiCall(urlWithParams);
            handleAndStoreLiveRampResponse(
                response,
                rampIdExpiryInMillis,
                isPairIdEnabled,
                isATSDEnabled,
            );

            trackLiveRampAction('LiveRamp_API_Completed', {
                duration: Date.now() - apiStartTime,
                hasResponse: !!response,
                envelopeCount: response?.envelopes?.length || 0,
            });
            return;
        } catch (error) {
            console.error('[Liveramp]Error initilizing', error);
            trackLiveRampAction('LiveRamp_API_Failed', {
                reason: 'exception',
                error:
                    error instanceof Error
                        ? error.message
                        : JSON.stringify(error),
                duration: Date.now() - apiStartTime,
            });
            return;
        }
    }

    function _getRampId(window: Window): string | undefined {
        const lr_env = window.document.cookie
            .split(';')
            .find(row => row.trim().startsWith('_lr_env'));
        return lr_env?.split('=')[1];
    }
    function _getPairId(window: Window): string | undefined {
        const lr_pairId = window.document.cookie
            .split(';')
            .find(row => row.trim().startsWith('_lr_pairid'));
        return lr_pairId?.split('=')[1];
    }
    function _getATSD(window: Window): string | undefined {
        const lr_atsd = window.document.cookie
            .split(';')
            .find(row => row.trim().startsWith('_lr_atsDirect'));
        return lr_atsd?.split('=')[1];
    }

    function _initLiveRampId(
        rampIdExpiryInMillis: number | null,
        isPairIdEnabled: boolean | null,
        isATSDEnabled: boolean | null,
    ) {
        return _invokeLiveRampAPI(
            rampIdExpiryInMillis,
            isPairIdEnabled,
            isATSDEnabled,
        );
    }
    function _deleteLiveRampCookies() {
        const expirationDate = new Date(0).toUTCString();
        document.cookie = `_lr_env=; expires=${expirationDate}; path=/;`;
        document.cookie = `_lr_env_exp=; expires=${expirationDate}; path=/;`;
        document.cookie = `_lr_pairid=; expires=${expirationDate}; path=/;`;
        document.cookie = `_lr_atsDirect=; expires=${expirationDate}; path=/;`;
    }

    return {
        initLiveRampId: _initLiveRampId,
        getRampId: _getRampId,
        getPairId: _getPairId,
        getATSD: _getATSD,
        deleteLiveRampCookies: _deleteLiveRampCookies,
    };
}

// eslint-disable-next-line @applitools/compat/no-async-await
const doLiveRampApiCall = async (liveRampUrl: string): Promise<any> => {
    try {
        const res = await fetch(liveRampUrl, {
            method: 'GET',
            headers: {
                Accept: 'application/json',
            },
        });

        if (!res.ok) {
            throw new Error(`HTTP error! status: ${res.status}`);
        }
        if (res.status === ENVELOP_NO_CONTENT_HTTP_STATUS) {
            return {};
        }

        const jsonResponse = await res.json();
        return jsonResponse;
    } catch (err) {
        console.error('[Liveramp]Error with API invocation', err);
        throw err;
    }
};

const buildQueryParam = (
    liveRampUrl: string,
    gdprConsent: string | undefined,
    hashedEmail: string,
): string => {
    const queryParams = new URLSearchParams();

    if (hashedEmail) {
        queryParams.append('it', '4');
        queryParams.append('iv', hashedEmail);
    }

    if (gdprConsent) {
        queryParams.append('cv', gdprConsent);
        queryParams.append('ct', '4');
    }
    queryParams.append('aType', '2');

    const separator = liveRampUrl.includes('?') ? '&' : '?';
    return `${liveRampUrl}${separator}${queryParams.toString()}`;
};

const handleAndStoreLiveRampResponse = (
    responseData: any,
    rampIdExpiryInMillis: number | null,
    isPairIdEnabled: boolean | null,
    isATSDEnabled: boolean | null,
) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    const envelope = responseData?.envelopes?.find(
        (envelope: any) =>
            envelope.type === SOURCE_ENVELOPE_LIVE_RAMP &&
            envelope.source === ENVELOP_SOURCE,
    )?.value;
    const pairId =
        isPairIdEnabled &&
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        responseData?.envelopes?.find(
            (envelope: any) =>
                envelope.type === SOURCE_PAIRID && envelope.source === PAIRID,
        )?.value;
    const atsDirect =
        isATSDEnabled &&
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        responseData?.envelopes?.find(
            (envelope: any) =>
                envelope.type === SOURCE_ATSD && envelope.source === ATSD,
        )?.value;

    calculateAndStoreEnvelopeTTL(
        envelope,
        pairId,
        rampIdExpiryInMillis,
        atsDirect,
    );
    return;
};

const calculateAndStoreEnvelopeTTL = (
    envelopeValue: string,
    pairId: string,
    rampIdExpiryInMillis: number | null,
    atsDirect: string | undefined,
) => {
    const expirationTime =
        new Date().getTime() +
        (rampIdExpiryInMillis || ENVELOPE_EXPIRY_IN_MILLISECONDS);
    const expirationDate = new Date(expirationTime).toUTCString();
    if (envelopeValue) {
        document.cookie = `_lr_env=${envelopeValue}; expires=${expirationDate}; path=/;`;
        document.cookie = `_lr_env_exp=${expirationTime}; path=/;`;
    } else {
        deleteCookie('_lr_env');
        deleteCookie('_lr_env_exp');
    }
    if (pairId) {
        document.cookie = `_lr_pairid=${pairId}; expires=${expirationDate}; path=/;`;
    } else {
        deleteCookie('_lr_pairid');
    }
    if (atsDirect) {
        document.cookie = `_lr_atsDirect=${atsDirect}; expires=${expirationDate}; path=/;`;
    } else {
        deleteCookie('_lr_atsDirect');
    }

    // Code from `_getRampId` is used here to verify that the RampID is correctly stored
    // in cookies and can be retrieved, and also to log the presence of RampID for New Relic tracking
    const lr_env = window.document.cookie
        .split(';')
        .find(row => row.trim().startsWith('_lr_env'));
    const storedRampId = lr_env?.split('=')?.[1];

    trackLiveRampAction('LiveRamp_Cookie_Set', {
        hasRampId: !!envelopeValue,
        hasPairId: !!pairId,
        hasAtsd: !!atsDirect,
        rampId: storedRampId,
    });
    return;
};

const deleteCookie = (name: string) => {
    document.cookie = `${name}=; expires=${new Date(0).toUTCString()}; path=/;`;
};
export const liverampInstance = getLiveRampEnvelops();
